// Die EINE Schaltflaeche des Webclients.
//
// WOFUER: ersetzt die im Audit gefundenen 58 verschiedenen Klassenketten fuer
// 91 Primaer-Schaltflaechen (559 rohe `<button>` insgesamt, davon 56 abweichende
// Destruktiv-Varianten). Ab hier gibt es genau vier Varianten und drei Groessen.
//
// Farben ausschliesslich ueber Tokens (KINETIC-FLOW-SYSTEM.md → Components →
// Buttons). Text auf gefuellten Flaechen nutzt `--color-on-primary` bzw.
// `--color-on-error` — niemals `text-white`.

import type { JSX } from "preact";
import type { LucideIcon } from "lucide-preact";

export type ButtonVariant = "primary" | "secondary" | "quiet" | "destructive";
export type ButtonSize = "xs" | "sm" | "md" | "lg";

type NativeButtonProps = Omit<
  JSX.IntrinsicElements["button"],
  "size" | "disabled" | "ref"
>;

export interface ButtonProps extends NativeButtonProps {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Fuehrendes lucide-Icon. Keine Emojis. */
  icon?: LucideIcon;
  /** Zeigt einen dezenten Spinner, setzt `aria-busy` und sperrt die Schaltflaeche. */
  loading?: boolean;
  disabled?: boolean;
}

// Gemeinsamer Sockel: alles, was jede Groesse und jede Variante teilt.
const BASE =
  "inline-flex items-center justify-center rounded font-medium transition-colors " +
  "focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2 " +
  "disabled:opacity-50 disabled:cursor-not-allowed";

// Die Mindesthoehe ist auf schmalen Fenstern groesser, damit Finger sie treffen
// (36px), auf breiten Fenstern kompakter (30px). Sie sitzt in den Groessen und
// NICHT im Sockel, weil `xs` sie bewusst nicht bekommt (siehe unten).
const MIN_H = "min-h-[36px] sm:min-h-[30px] ";

const SIZES: Record<ButtonSize, string> = {
  // `xs` hat DIESELBE Geometrie wie `sm`, aber KEINE Mindesthoehe — und
  // unterschreitet damit bewusst die Touch-Mindestgroesse.
  //
  // WOFUER: ausschliesslich Bedienelemente in dichten Tabellenzeilen und
  // Aufklapp-Panels der Verwaltung (Status-, Zyklen-, Felder-, Spaces-Listen).
  // Dort steht der Knopf in einer 24px hohen Zeile neben Text; die Mindesthoehe
  // von `sm` machte jede dieser Zeilen 6px hoeher — eine sichtbare
  // Darstellungsaenderung, an der die Umstellung vorher gescheitert ist.
  //
  // NICHT fuer primaere Aktionen (Dialog-Fusszeilen, Formular-Absenden,
  // Seiten-Aktionen) verwenden — die brauchen die Touch-Groesse und nehmen
  // `md` oder `lg`.
  xs: "gap-1.5 px-2.5 py-1 text-xs",
  sm: MIN_H + "gap-1.5 px-2.5 py-1 text-xs",
  md: MIN_H + "gap-1.5 px-3 py-1.5 text-xs",
  lg: MIN_H + "gap-2 px-4 py-2 text-sm",
};

// Icon-Groessen nur 14/16/20/24 laut Design-Dokument.
const ICON_SIZES: Record<ButtonSize, 14 | 16> = {
  xs: 14,
  sm: 14,
  md: 16,
  lg: 16,
};

const VARIANTS: Record<ButtonVariant, string> = {
  primary:
    "bg-[color:var(--color-primary)] text-[color:var(--color-on-primary)] " +
    "hover:bg-[color:var(--color-primary-strong)]",
  secondary:
    "border border-[color:var(--color-outline-variant)] bg-transparent " +
    "text-[color:var(--color-on-surface)] " +
    "hover:bg-[color:var(--color-surface-container-low)] hover:border-[color:var(--color-outline)]",
  quiet:
    "border border-transparent bg-transparent text-[color:var(--color-on-surface-variant)] " +
    "hover:bg-[color:var(--color-surface-container-low)] hover:text-[color:var(--color-on-surface)]",
  destructive:
    "bg-[color:var(--color-error)] text-[color:var(--color-on-error)] " +
    "hover:opacity-90",
};

/** Dezenter Spinner in der aktuellen Textfarbe (`currentColor`). */
function Spinner(props: { size: number }): JSX.Element {
  return (
    <svg
      class="animate-spin"
      width={props.size}
      height={props.size}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="3" opacity="0.25" />
      <path
        d="M21 12a9 9 0 0 0-9-9"
        stroke="currentColor"
        stroke-width="3"
        stroke-linecap="round"
      />
    </svg>
  );
}

export function Button(props: ButtonProps): JSX.Element {
  const {
    variant = "primary",
    size = "md",
    icon: Icon,
    loading = false,
    disabled = false,
    class: klass,
    className,
    children,
    type,
    ...rest
  } = props;

  const blocked = disabled || loading;
  const extra = [klass, className].filter(Boolean).join(" ");
  const cls = `${BASE} ${SIZES[size]} ${VARIANTS[variant]}${extra ? ` ${extra}` : ""}`;

  return (
    <button
      {...rest}
      type={type ?? "button"}
      class={cls}
      disabled={blocked}
      aria-busy={loading ? "true" : undefined}
    >
      {loading ? (
        <Spinner size={ICON_SIZES[size]} />
      ) : Icon ? (
        <Icon size={ICON_SIZES[size]} aria-hidden="true" />
      ) : null}
      {children}
    </button>
  );
}
